import type {
  ActivityBookableOption,
  ActivityBookableOptionTicket,
  ActivityIngredientAdapter,
  ActivityIngredientResponse,
  GraphQL,
  UnavailableActivityIngredientResponse,
} from '@libs/trip-planner-interfaces';
import { IngredientType, useFragment } from '@libs/trip-planner-interfaces';

import { assertSmartSearchList, assertSmartSearchValue } from '../../utils';
import { ActivitySubIngredientFragment } from '../../queries';

import type { IngredientResponseQL } from './itineraryResponseQL';
import { toMoney } from './moneyResponseQL';

type IngredientWithSubIngredientResponseQL = Extract<
  IngredientResponseQL,
  { subIngredientResults?: unknown }
>;
type SubIngredientResponsesQL = NonNullable<
  IngredientWithSubIngredientResponseQL['subIngredientResults']
>;
type SubIngredientResponseQL = NonNullable<SubIngredientResponsesQL[number]>;
type ActivityIngredientResponseQL = Extract<
  SubIngredientResponseQL,
  { type: 'TicketIngredientResponseQL' }
>;
export function toActivityIngredientResponse(
  result: ActivityIngredientResponseQL,
  ingredient: ActivityIngredientAdapter
): ActivityIngredientResponse {
  const activityIngredientResult = useFragment(
    ActivitySubIngredientFragment,
    result
  );

  const unavailableActivityIngredientResponse =
    toUnavailableActivityIngredientResponse(
      activityIngredientResult,
      ingredient
    );

  if (activityIngredientResult.suggested) {
    return {
      ...unavailableActivityIngredientResponse,
      isAvailable: true,
      suggested: getSuggestedActivityBookableOption(
        activityIngredientResult.suggested
      ),
      alternativeSuggestion: null,
    };
  } else if (activityIngredientResult.alternativeSuggestion) {
    return {
      ...unavailableActivityIngredientResponse,
      alternativeSuggestion: getAlternativeActivityBookableOption(
        activityIngredientResult.alternativeSuggestion
      ),
    };
  } else {
    return unavailableActivityIngredientResponse;
  }
}

type SuggestedActivityQL = NonNullable<
  GraphQL.ActivitySubIngredientFragment['suggested']
>;
function getSuggestedActivityBookableOption(
  suggested: SuggestedActivityQL
): ActivityBookableOption {
  assertSmartSearchValue(suggested.prices?.totalPrice);
  assertSmartSearchList(suggested.activityTicketResults);

  return {
    code: suggested.code,
    totalPrice: toMoney(suggested.prices.totalPrice),
    addToBasketToken: '',
    tickets: suggested.activityTicketResults.map(
      getActivityBookableOptionTicket
    ),
  };
}

type AlternativeActivitySuggestionQL = NonNullable<
  GraphQL.ActivitySubIngredientFragment['alternativeSuggestion']
>;
function getAlternativeActivityBookableOption(
  alternativeSuggestion: AlternativeActivitySuggestionQL
): ActivityBookableOption {
  assertSmartSearchValue(
    alternativeSuggestion.prices?.totalPriceWithAlternative
  );
  assertSmartSearchList(alternativeSuggestion.activityTicketResults);

  return {
    code: alternativeSuggestion.code,
    totalPrice: toMoney(alternativeSuggestion.prices.totalPriceWithAlternative),
    addToBasketToken: '',
    tickets: alternativeSuggestion.activityTicketResults.map(
      getActivityBookableOptionTicket
    ),
  };
}

type ActivityTicketResultsQL = NonNullable<
  | AlternativeActivitySuggestionQL['activityTicketResults']
  | SuggestedActivityQL['activityTicketResults']
>;
type ActivityTicketResultQL = NonNullable<ActivityTicketResultsQL[number]>;
function getActivityBookableOptionTicket(
  activityTicketResultQL: ActivityTicketResultQL
): ActivityBookableOptionTicket {
  return {
    ageRangeId: activityTicketResultQL.ageRangeId,
    title: activityTicketResultQL.title,
    rateId: activityTicketResultQL.rateId,
    description: activityTicketResultQL.description,
    timeSlotId: activityTicketResultQL.timeSlotId ?? undefined,
    timeSlotStartTime: activityTicketResultQL.timeSlotStartTime ?? undefined,
    supplierToken: activityTicketResultQL.supplierToken ?? undefined,
  };
}

function toUnavailableActivityIngredientResponse(
  result: GraphQL.ActivitySubIngredientFragment,
  ingredient: ActivityIngredientAdapter
): UnavailableActivityIngredientResponse {
  assertSmartSearchValue(result.date);

  return {
    ingredientId: ingredient.parentIngredientId,
    ingredientType: IngredientType.Activity,
    date: new Date(result.date),
    options: [],
    subIngredientResponse: [],
    suggested: null,
    isAvailable: false,
    alternativeSuggestion: null,
  };
}
